import React from 'react';
import { AlertTriangle, Download, X } from 'lucide-react';

/**
 * Pre-upload warning shown when an artist picks a file that's much larger
 * than necessary for smooth playback. Non-blocking — they can choose to
 * proceed anyway. The goal is to educate artists on export settings so we
 * don't have to spend server CPU re-compressing every upload.
 *
 * Triggers when:
 *   • file size > 150 MB, OR
 *   • estimated bitrate (size / duration) > 8 Mbps for any resolution
 *
 * For context: a well-encoded 1080p30 H.264 file at 4 Mbps for 3 minutes
 * is ~90 MB. Anything materially larger is wasted bandwidth.
 */
export const OversizeFileWarning = ({ open, stats, onProceed, onPickDifferent }) => {
  if (!open || !stats) return null;
  const { sizeMB, durationSec, estBitrateKbps, width, height } = stats;
  const minutes = Math.floor(durationSec / 60);
  const seconds = Math.round(durationSec % 60);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="oversize-file-warning"
    >
      <div className="relative w-full max-w-lg bg-zinc-900 rounded-2xl border border-amber-500/40 overflow-hidden">
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="font-semibold text-white">This file is larger than recommended</p>
          </div>
          <button
            onClick={onPickDifferent}
            className="p-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
            data-testid="oversize-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-zinc-800/60 border border-zinc-800 px-2 py-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Size</p>
              <p className="text-sm font-semibold text-amber-300">{Math.round(sizeMB)} MB</p>
            </div>
            <div className="rounded-lg bg-zinc-800/60 border border-zinc-800 px-2 py-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Length</p>
              <p className="text-sm font-semibold text-zinc-200">{minutes}:{String(seconds).padStart(2, '0')}</p>
            </div>
            <div className="rounded-lg bg-zinc-800/60 border border-zinc-800 px-2 py-2">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Bitrate</p>
              <p className="text-sm font-semibold text-amber-300">
                {estBitrateKbps ? `~${Math.round(estBitrateKbps / 1000)} Mbps` : '—'}
              </p>
            </div>
          </div>

          <p className="text-sm text-zinc-300 leading-relaxed">
            Your file is roughly <span className="text-amber-300 font-medium">{Math.round(sizeMB / 70)}× larger</span>{' '}
            than it needs to be for smooth playback. We'll re-compress it on our servers (takes 2-5 min),
            but you'll get a better upload experience — and your fans will get faster playback — if you
            export at the recommended settings below.
          </p>

          <div className="rounded-lg bg-cyan-500/10 border border-cyan-500/30 px-4 py-3 space-y-2">
            <p className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">Recommended export settings</p>
            <ul className="text-xs text-zinc-300 space-y-1 list-disc list-inside">
              <li>Resolution: <span className="text-white font-mono">1080p ({width}×{height} is good)</span></li>
              <li>Codec: <span className="text-white font-mono">H.264 (Main or High profile, Level 4.0)</span></li>
              <li>Video bitrate: <span className="text-white font-mono">3–5 Mbps</span></li>
              <li>Audio: <span className="text-white font-mono">AAC, 128 kbps, 44.1 kHz</span></li>
              <li>Container: <span className="text-white font-mono">.mp4 with faststart</span></li>
            </ul>
          </div>

          <a
            href="https://handbrake.fr/downloads.php"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium"
            data-testid="oversize-handbrake-link"
          >
            <Download className="w-3.5 h-3.5" />
            Get Handbrake — free, easy presets that match the above
          </a>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <button
              onClick={onPickDifferent}
              className="flex-1 px-4 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-medium text-sm"
              data-testid="oversize-pick-different"
            >
              Pick a different file
            </button>
            <button
              onClick={onProceed}
              className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm"
              data-testid="oversize-proceed"
            >
              Upload this file anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
